import * as React from 'react';
import { CalendarIcon } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { toISODateInTZ } from '@/utils/helpers';

interface DatePickerProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  'data-test-id'?: string;
  disabled?: boolean;
}

export function DatePicker({
  value,
  onChange,
  placeholder,
  'data-test-id': dataTestId,
  disabled,
}: DatePickerProps) {
  const selected = React.useMemo(() => {
    if (!value) return undefined;
    // Parse as local date to avoid UTC midnight off-by-one in UTC+1/UTC+2
    const datePart = value.split('T')[0];
    const [y, m, day] = datePart.split('-').map(Number);
    if (!y || !m || !day) return undefined;
    const d = new Date(y, m - 1, day);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  function formatDisplay(d?: Date) {
    if (!d) return placeholder || 'Input Date';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn('w-full justify-start text-left font-normal')}
          data-test-id={dataTestId || 'date-picker-trigger'}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDisplay(selected)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d: Date | undefined) => {
            const iso = d ? toISODateInTZ(d) : '';
            onChange(iso);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
